/* Source: https://michaelchan-13570.medium.com/using-react-router-v4-prompt-with-custom-modal-component-ca839f5faf39 */
import { Location } from "history";
import { noop } from "lodash";
import { useEffect, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@material-ui/core";

interface Props {
  when?: boolean;
  shouldBlockNavigation?: (location: Location) => boolean;
  title?: string;
  body?: string;
  cancelButtonText?: string;
  continueButtonText?: string;
  onCancel?: () => any;
}

export default function RouteLeavingGuard({
  when = true,
  shouldBlockNavigation = () => true,
  title = "You have unsaved changes",
  body = "Are you sure you want to discard your changes?",
  cancelButtonText = "Go Back",
  continueButtonText = "Discard & Continue",
  onCancel = noop
}: Props): JSX.Element {
  const history = useHistory();

  const [modalVisible, setModalVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const closeModal = () => {
    setModalVisible(false);
    onCancel();
  };

  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    if (
      history.location.pathname === nextLocation.pathname &&
      history.location.search === nextLocation.search
    ) {
      // just a change in the hash; no need to block navigation
      return true;
    }

    if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
      setModalVisible(true);
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  const handleConfirmNavigationClick = () => {
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Navigate to the previous blocked location with your navigate function
      history.push(lastLocation.pathname);
    }
  }, [confirmedNavigation, lastLocation, history]);

  useEffect(() => {
    if (when) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }

    () => {
      window.onbeforeunload = null;
    };
  }, [when]);

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />
      <Dialog
        open={modalVisible}
        onClose={closeModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {body}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="primary">
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleConfirmNavigationClick}
            color="primary"
            autoFocus
          >
            {continueButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
